import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { ProductCardSectionLayout } from '../../../visual-components/product-card-section-layout/product-card-section-layout';
import { SectionHeader } from '../../../visual-components/section-header/section-header';
import { ProductCard } from '../../../visual-components/product-card/product-card';
import { GridContainer, GridItem } from '../../../visual-components/grid/grid';
import { Spacer } from '../../../visual-components/spacer/spacer';
import { Pagination } from '../../../visual-components/pagination/pagination';
import { GetArticlesResponse } from '../../../modules/backend/openapi/api-types';
import { EmptyState } from '../../../visual-components/empty-state/empty-state';
import { Button, ButtonLink } from '../../../visual-components/button/button';
import { useFormatter } from '../../../core/formatter/use-formatter';
import { usePathBuilder } from '../../../core/routing/hooks/use-path-builder';

type Props = {
  articles: GetArticlesResponse;
  page: number;
  setPage: (page: number) => void;
  resetFilters: () => void;
  error?: Error | null | unknown;
};

export const ResultSection: React.FC<Props> = ({ articles, page, setPage, resetFilters, error }) => {
  const { t } = useTranslation();
  const { formatInteger } = useFormatter();
  const { formArticleNotFound } = usePathBuilder();

  const scrollRef = useRef<HTMLDivElement>(null);

  if (articles.items.length === 0) {
    return (
      <GridContainer>
        <GridItem span="12">
          <EmptyState description={t('search.noResults.text')} title={t('search.noResults.title')}>
            <Button
              icon="arrow-circle"
              onClick={() => {
                resetFilters();
              }}
            >
              {t('search.resetAllFilters')}
            </Button>
            <ButtonLink icon="mail" to={formArticleNotFound()} variant="secondary">
              {t('search.requestArticle')}
            </ButtonLink>
          </EmptyState>
        </GridItem>
      </GridContainer>
    );
  }

  return (
    <>
      <GridContainer>
        <GridItem>
          <ProductCardSectionLayout
            error={error}
            sectionHeader={
              <SectionHeader ref={scrollRef}>
                {t('search.resultsFound', {
                  count: articles.meta.pagination.totalCount,
                  number: formatInteger(articles.meta.pagination.totalCount),
                })}
              </SectionHeader>
            }
          >
            {articles.items.map(
              ({
                id,
                itemDesc2,
                itemDesc3,
                gloss,
                surface,
                effect,
                bonding,
                hexCode,
                series: { name, number, numberColor },
                weatherResistance,
                hasRendering,
              }) => (
                <ProductCard
                  key={id}
                  articleId={id}
                  bonding={bonding ?? undefined}
                  effect={effect ?? undefined}
                  gloss={gloss ?? undefined}
                  hasRendering={hasRendering}
                  hexCode={hexCode ?? undefined}
                  itemDesc2={itemDesc2}
                  itemDesc3={itemDesc3}
                  series={name}
                  seriesNumber={number}
                  seriesNumberColor={numberColor ?? undefined}
                  surface={surface ?? undefined}
                  title={id}
                  weatherResistance={weatherResistance ?? undefined}
                />
              ),
            )}
          </ProductCardSectionLayout>
        </GridItem>
      </GridContainer>
      <GridContainer>
        <GridItem span="12">
          <Spacer size="08" />
          <Pagination
            currentPage={page}
            lastPage={articles.meta.pagination.pageCount}
            setPage={(page) => {
              setPage(page);
              scrollRef.current?.scrollIntoView({ behavior: 'smooth' });
            }}
          />
        </GridItem>
      </GridContainer>
    </>
  );
};
